import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import _ from "lodash";
import Session from "users/__session/session";
import User from "users/user";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/_shared/masked-fields";
import ProposalBuilderSchema, { ProposalBuilderJSON } from "work/proposals/_schemas/proposal-builder-schema";
import ProposalSchema, { ProposalParams } from "work/proposals/_schemas/proposal-schema";
import Proposal from "work/proposals/proposal";
import { ProposalFieldJSON } from "work/proposals/proposal-fields/_schema/proposal-field-schema";
import ProposalField, { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";

interface ProposalSpecChangeJSON {
  field: ProposalFieldJSON;
  action: keyof typeof ProposalChangeAction;
  value: string;
}

export class ProposalSpecChange {
  field: ProposalField;
  action: ProposalChangeAction;
  value: string;
  timeStamp: AHDate = new AHDate();

  constructor(field: ProposalField, action: ProposalChangeAction, value: string) {
    this.field = field ?? null;
    this.action = action ?? ProposalChangeAction.Edit;
    this.value = value ?? "";
  }

  public static fromJSON(json: ProposalSpecChangeJSON): ProposalSpecChange | undefined {
    const field = ProposalField.fromJSON(json.field);
    if (!field) return undefined;
    const action = ProposalChangeAction.fromJSON(json.action);
    const value = json.value;
    return new ProposalSpecChange(field, action, value);
  }

  public toJSON(): ProposalSpecChangeJSON {
    return {
      field: this.field?.toJSON() ?? null,
      action: this.action.toString() as keyof typeof ProposalChangeAction,
      value: this.value,
    };
  }

  public clone(): ProposalSpecChange {
    const clone = ProposalSpecChange.fromJSON(this.toJSON());
    if (!clone) throw new Error("Failed to clone ProposalSpecChange");
    return clone;
  }
}

export type ProposalChangeActionPresentTense = "Edit" | "ReEdit" | "Add" | "Remove";
export class ProposalChangeAction {
  // The present tense of the action MUST match the readonly property name
  static readonly Edit: ProposalChangeAction = new ProposalChangeAction("Edit", "Edited");
  static readonly ReEdit: ProposalChangeAction = new ProposalChangeAction("ReEdit", "Re-edited");
  static readonly Add: ProposalChangeAction = new ProposalChangeAction("Add", "Added");
  static readonly Remove: ProposalChangeAction = new ProposalChangeAction("Remove", "Removed");

  private constructor(
    private readonly presentTense: ProposalChangeActionPresentTense,
    public readonly actionDescription: string,
  ) {}

  public toString(): ProposalChangeActionPresentTense {
    return this.presentTense;
  }

  public static fromJSON(obj: keyof typeof ProposalChangeAction): ProposalChangeAction {
    return ProposalChangeAction[obj] as ProposalChangeAction;
  }
}

export default class ProposalBuilder {
  public readonly session: Session;

  public constructor(params: {
    session: Session;
    startingSpec?: ProposalParams;
    specStack?: ProposalParams[];
    existingWorkAgreement?: WorkAgreement;
    preSelectedClientLeader?: EntityMember;
    preSelectedVendorLeader?: EntityMember;
    preSelectedTeam?: VendorTeam;
    lastUpdated?: AHDate;
    currentIndex?: number;
  }) {
    const {
      startingSpec,
      specStack,
      session,
      lastUpdated,
      existingWorkAgreement,
      preSelectedClientLeader,
      preSelectedVendorLeader,
      preSelectedTeam,
      currentIndex,
    } = params;
    if (!session.user?.id) {
      throw new Error("A valid session with a current user is required to create a ProposalBuilder.");
    }
    this.session = session;
    this._lastUpdated = lastUpdated ?? new AHDate();
    this._currentUser = session.user;
    this._proposalSchema = new ProposalSchema(new WorkAgreementSchema(), session.user);

    if (specStack) {
      this._specStack = specStack;
      this._currentIndex = currentIndex ?? Math.max(specStack.length - 1, 0);
      return;
    }

    let initialSpec: ProposalParams = {
      ...Proposal.getDefaultSpec(session.user.toEntityMember()),
      creator: this._currentUser.toEntityMember(),
    };

    if (existingWorkAgreement) {
      initialSpec = {
        ...initialSpec,
        workAgreement: existingWorkAgreement,
        clientReviewers: [],
        vendorReviewers: [],
        negotiable: new AHBoolean(true),
      };
    }
    if (startingSpec) {
      initialSpec = startingSpec;
    }
    this._specStack = [initialSpec];

    let modifiedProposalBuilder: ProposalBuilder | undefined;
    if (preSelectedVendorLeader) {
      modifiedProposalBuilder = this.setVendorTeam(new VendorTeam({ leader: preSelectedVendorLeader, members: [] }));
    }

    if (preSelectedClientLeader) {
      modifiedProposalBuilder = modifiedProposalBuilder
        ? modifiedProposalBuilder.setClient(preSelectedClientLeader)
        : this.setClient(preSelectedClientLeader);
    }

    if (!this.currentSpec.workAgreement.clientTeam && !session.context?.viewingAsVendor) {
      modifiedProposalBuilder = modifiedProposalBuilder
        ? modifiedProposalBuilder.setClient(
            new EntityMember({
              legalEntityId: session.user.legalEntityId,
              userId: session.user.id,
            }),
          )
        : this.setClient(
            new EntityMember({
              legalEntityId: session.user.legalEntityId,
              userId: session.user.id,
            }),
          );
    }

    if (preSelectedTeam) {
      modifiedProposalBuilder = modifiedProposalBuilder
        ? modifiedProposalBuilder.setVendorTeam(preSelectedTeam)
        : this.setVendorTeam(preSelectedTeam);
    }

    if (modifiedProposalBuilder) {
      this._specStack = modifiedProposalBuilder._specStack;
      this._currentIndex = modifiedProposalBuilder._currentIndex;
      this._sessionHistory = modifiedProposalBuilder._sessionHistory;
    }
  }

  public get specStack(): ProposalParams[] {
    return this._specStack.map((spec) => ({
      ...spec,
    }));
  }

  public get currentSpec(): ProposalParams {
    if (this._currentIndex >= this._specStack.length || this._currentIndex < 0) throw new Error("Invalid index");
    return this._specStack[this._currentIndex];
  }

  public get currentIndex(): number {
    return this._currentIndex;
  }

  public get sessionHistory(): ProposalSpecChange[] {
    return this._sessionHistory;
  }

  public isDifferentFrom(other?: ProposalBuilder | null): boolean {
    if (isNullOrUndefined(other)) {
      return true;
    }
    const thisSpecJSON = this._proposalSchema.serialize(this.currentSpec);
    const otherSpecJSON = this._proposalSchema.serialize(other.currentSpec);
    return !_.isEqual(thisSpecJSON, otherSpecJSON);
  }

  public get lastUpdated(): AHDate {
    return this._lastUpdated;
  }

  public cleanStack(): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder._specStack = [newBuilder.currentSpec];
    newBuilder._currentIndex = 0;
    return newBuilder;
  }

  public setClient(client?: EntityMember): ProposalBuilder {
    if (Masked.isMasked(this.currentSpec.workAgreement.clientTeam)) {
      throw new Error("Cannot set client when current client team is masked");
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        clientTeam: new ClientTeam({
          members: this.currentSpec.workAgreement.clientTeam?.members ?? [],
          leader: client,
        }),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setName(name: ProjectName): ProposalBuilder {
    if (this.currentSpec.workAgreement.name?.value === name.value) {
      return this;
    }
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        name: name,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Name),
    );
    if (
      (!newBuilder._specStack[0].workAgreement.name?.value && !name.value) ||
      newBuilder._specStack[0].workAgreement.name?.isEqualTo(name)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].workAgreement.name?.value ?? "";
    if (!newBuilder._specStack[0].workAgreement.name && name) {
      action = ProposalChangeAction.Add;
      value = name.value;
    } else if (newBuilder._specStack[0].workAgreement.name?.value && !name.value) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(ProposalField.Name, action, value);
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setDescription(description: ProjectDescription): ProposalBuilder {
    if (
      (!description && !this.currentSpec.workAgreement.description) ||
      this.currentSpec.workAgreement.description?.isEqualTo(description)
    ) {
      return this;
    }
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        description: description,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Description),
    );

    if (
      (!newBuilder._specStack[0].workAgreement.description?.value && !description?.value) ||
      newBuilder._specStack[0].workAgreement.description?.isEqualTo(description)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].workAgreement.description?.value ?? "";
    if (!newBuilder._specStack[0].workAgreement.description?.value && description?.value) {
      action = ProposalChangeAction.Add;
      value = description.value;
    } else if (newBuilder._specStack[0].workAgreement.description?.value && !description?.value) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(ProposalField.Description, action, value);
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setNegotiable(negotiable: boolean): ProposalBuilder {
    if (this.currentSpec.negotiable.value === negotiable) {
      return this;
    }

    const newBuilder = this.clone();

    this._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Negotiable),
    );
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      negotiable: new AHBoolean(negotiable),
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    if (newBuilder._specStack[0].negotiable.value === negotiable) {
      return newBuilder;
    }
    const action = ProposalChangeAction.Edit;
    const specChange = new ProposalSpecChange(
      ProposalField.Negotiable,
      action,
      newBuilder._specStack[0].negotiable ? "Negotiable" : "Not Negotiable",
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setResponseDueBy(responseDueBy: AHDate | undefined): ProposalBuilder {
    if (
      (!this.currentSpec.responseDueBy && !responseDueBy) ||
      this.currentSpec.responseDueBy?.isEqualTo(responseDueBy)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      responseDueBy: responseDueBy,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.ResponseDueBy),
    );

    if (
      (!newBuilder._specStack[0].responseDueBy && !responseDueBy) ||
      newBuilder._specStack[0].responseDueBy?.isEqualTo(responseDueBy)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].responseDueBy?.format("MM/DD/YY") ?? "";
    if (!newBuilder._specStack[0].responseDueBy && responseDueBy) {
      action = ProposalChangeAction.Add;
      value = responseDueBy.format("MM/DD/YY");
    } else if (newBuilder._specStack[0].responseDueBy && !responseDueBy) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(ProposalField.ResponseDueBy, action, value);
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setStartDate(startDate: AHDate | undefined): ProposalBuilder {
    if (
      (!this.currentSpec.workAgreement.startDate && !startDate) ||
      this.currentSpec.workAgreement.startDate?.isEqualTo(startDate)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        startDate: startDate,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.StartDate),
    );
    if (
      (!newBuilder._specStack[0].workAgreement.startDate && !startDate) ||
      newBuilder._specStack[0].workAgreement.startDate?.isEqualTo(startDate)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].workAgreement.startDate?.format("MM/DD/YY") ?? "";
    if (!newBuilder._specStack[0].workAgreement.startDate && startDate) {
      action = ProposalChangeAction.Add;
      value = startDate.format("MM/DD/YY");
    } else if (newBuilder._specStack[0].workAgreement.startDate && !startDate) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(ProposalField.StartDate, action, value);
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setEndDate(endDate: AHDate | undefined): ProposalBuilder {
    if (
      (!this.currentSpec.workAgreement.endDate && !endDate) ||
      this.currentSpec.workAgreement.endDate?.isEqualTo(endDate)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        endDate: endDate,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.EndDate),
    );

    if (
      (!newBuilder._specStack[0].workAgreement.endDate && !endDate) ||
      newBuilder._specStack[0].workAgreement.endDate?.isEqualTo(endDate)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].workAgreement.endDate?.format("MM/DD/YY") ?? "";
    if (!newBuilder._specStack[0].workAgreement.endDate && endDate) {
      action = ProposalChangeAction.Add;
      value = endDate.format("MM/DD/YY");
    } else if (newBuilder._specStack[0].workAgreement.endDate && !endDate) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(ProposalField.EndDate, action, value);
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setVendorTeam(newTeam: VendorTeam | undefined): ProposalBuilder {
    if (
      Masked.isMasked(this.currentSpec.workAgreement.vendorTeam) ||
      (!newTeam && !this.currentSpec.workAgreement.vendorTeam) ||
      this.currentSpec.workAgreement.vendorTeam?.isEqualTo(newTeam) ||
      Masked.isMasked(this._specStack[0].workAgreement.vendorTeam)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        vendorTeam: newTeam,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.category !== ProposalFieldCategory.Team,
    );

    if (Masked.isMasked(newSpec.workAgreement.teamRestricted)) {
      throw new Error("Cannot compare team restriction changes when new team restriction is masked");
    }
    if (Masked.isMasked(newBuilder._specStack[0].workAgreement.vendorTeam)) {
      throw new Error("Cannot compare team changes when original team is masked");
    }
    if (Masked.isMasked(newBuilder._specStack[0].workAgreement.teamRestricted)) {
      throw new Error("Cannot compare team restriction changes when original team restriction is masked");
    }
    if (Masked.isMasked(newBuilder._specStack[0].workAgreement.discount)) {
      throw new Error("Cannot compare discount changes when original discount is masked");
    }

    const addedLeader =
      newTeam?.leader?.userId.value && !newBuilder._specStack[0].workAgreement.vendorTeam?.leader?.userId.value;

    const removedLeader =
      !newTeam?.leader?.userId.value && newBuilder._specStack[0].workAgreement.vendorTeam?.leader?.userId.value;

    if (
      addedLeader &&
      newTeam.leader &&
      !newTeam.leader?.userId.isEqualTo(newBuilder._specStack[0].workAgreement.vendorTeam?.leader?.userId)
    ) {
      const specChange = new ProposalSpecChange(
        ProposalField.TeamLeader,
        ProposalChangeAction.Add,
        "Team leader added.",
      );
      newBuilder._sessionHistory.push(specChange);
    }

    if (removedLeader && newBuilder._specStack[0].workAgreement.vendorTeam?.leader) {
      const specChange = new ProposalSpecChange(
        ProposalField.TeamLeader,
        ProposalChangeAction.Remove,
        "Team leader removed.",
      );
      newBuilder._sessionHistory.push(specChange);
    }

    for (const newMember of newTeam?.members ?? []) {
      const existingMember = newBuilder._specStack[0].workAgreement.vendorTeam?.members.some((originalMember) =>
        originalMember.userId.isEqualTo(newMember.userId),
      );
      if (!existingMember) {
        const specChange = new ProposalSpecChange(
          ProposalField.TeamMember(newMember.userId),
          ProposalChangeAction.Add,
          "Team member added.",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const originalMember of newBuilder._specStack[0].workAgreement.vendorTeam?.members ?? []) {
      const removedMember = !newTeam?.members.some((newMember) => newMember.userId.isEqualTo(originalMember.userId));

      if (removedMember) {
        const specChange = new ProposalSpecChange(
          ProposalField.TeamMember(originalMember.userId),
          ProposalChangeAction.Remove,
          "Team member removed.",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    if (newBuilder._specStack[0]?.workAgreement.teamRestricted?.value !== newSpec.workAgreement.teamRestricted?.value) {
      const specChange = new ProposalSpecChange(
        ProposalField.TeamRestriction,
        newSpec.workAgreement.teamRestricted?.value ? ProposalChangeAction.Add : ProposalChangeAction.Remove,
        "Team Restriction",
      );
      newBuilder._sessionHistory.push(specChange);
    }
    return newBuilder;
  }

  public setFeeSchedule(
    categories: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | undefined,
  ): ProposalBuilder {
    const newBuilder = this.clone();
    if (Masked.isMasked(newBuilder._specStack[0].workAgreement.feeSchedule)) {
      throw new Error("Cannot compare fee schedule changes when original fee schedule is masked");
    }

    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        feeSchedule: categories ?? [],
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.category !== ProposalFieldCategory.FeeSchedule,
    );
    for (const category of categories ?? []) {
      const existingCategory = newBuilder._specStack[0].workAgreement.feeSchedule?.find((cat) =>
        cat.id.isEqualTo(category.id),
      );
      if (!existingCategory) {
        const specChange = new ProposalSpecChange(
          ProposalField.FeeScheduleCategory(category.id),
          ProposalChangeAction.Add,
          category.toString(),
        );
        newBuilder._sessionHistory.push(specChange);
      } else if (!category.isEqualTo(existingCategory)) {
        const specChange = new ProposalSpecChange(
          ProposalField.FeeScheduleCategory(category.id),
          ProposalChangeAction.Edit,
          existingCategory.toString(),
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const category of newBuilder._specStack[0].workAgreement.feeSchedule ?? []) {
      const removedCategory = !categories?.some((cat) => cat.id.isEqualTo(category.id));
      if (removedCategory) {
        const specChange = new ProposalSpecChange(
          ProposalField.FeeScheduleCategory(category.id),
          ProposalChangeAction.Remove,
          category.toString(),
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    return newBuilder;
  }

  public setDiscount(discount: Percent): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        discount: discount ?? 0,
      },
    };

    if (
      Masked.isMasked(newSpec.workAgreement.discount) ||
      Masked.isMasked(newBuilder._specStack[0].workAgreement.discount)
    ) {
      throw new Error("Cannot compare discount changes when new discount is masked");
    }

    if (
      (!newBuilder.currentSpec.workAgreement.discount && !discount) ||
      newBuilder.currentSpec.workAgreement.discount?.isEqualTo(discount) ||
      Masked.isMasked(this._specStack[0].workAgreement.discount)
    ) {
      return this;
    }

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Discount),
    );

    if (
      (!newBuilder._specStack[0].workAgreement.discount && !discount) ||
      newBuilder._specStack[0].workAgreement.discount?.isEqualTo(discount)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].workAgreement.discount?.toString() ?? "";
    if (!newBuilder._specStack[0].workAgreement.discount?.value && discount) {
      action = ProposalChangeAction.Add;
      value = discount.toString();
    } else if (newBuilder._specStack[0].workAgreement.discount?.value && !discount.value) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(ProposalField.Discount, action, value);
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setConflicts(documents: WorkDocument[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    if (
      Masked.isMasked(newBuilder.currentSpec?.workAgreement.conflictsCheckWaived) ||
      Masked.isMasked(newBuilder.currentSpec?.workAgreement.conflicts)
    ) {
      throw new Error(
        "Cannot set conflicts documents when current conflicts documents or conflicts check waiver is masked",
      );
    }

    if (
      Masked.isMasked(newBuilder._specStack[0]?.workAgreement.conflictsCheckWaived) ||
      Masked.isMasked(newBuilder._specStack[0]?.workAgreement.conflicts)
    ) {
      return this;
    }

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        conflicts: _.uniqBy(documents, (document) => document.id.value),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.category !== ProposalFieldCategory.Conflicts,
    );
    for (const document of documents) {
      const existingDocument = newBuilder._specStack[0].workAgreement.conflicts?.some((doc) =>
        doc.id.isEqualTo(document.id),
      );
      if (!existingDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ConflictsDocument(document.id),
          ProposalChangeAction.Add,
          document.name?.value ?? "",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const document of newBuilder._specStack[0]?.workAgreement.conflicts ?? []) {
      const removedDocument = !documents.some((doc) => doc.id.isEqualTo(document.id));
      if (removedDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ConflictsDocument(document.id),
          ProposalChangeAction.Remove,
          document.name?.value ?? "",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    if (
      newBuilder._specStack[0]?.workAgreement.conflictsCheckWaived?.value !==
      newBuilder.currentSpec?.workAgreement.conflictsCheckWaived?.value
    ) {
      const specChange = new ProposalSpecChange(
        ProposalField.WaiveConflictsCheck,
        newBuilder.currentSpec?.workAgreement.conflictsCheckWaived?.value
          ? ProposalChangeAction.Add
          : ProposalChangeAction.Remove,
        "Conflicts Check Waiver",
      );
      newBuilder._sessionHistory.push(specChange);
    }
    return newBuilder;
  }

  public setClientPolicies(documents: WorkDocument[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    if (
      Masked.isMasked(newBuilder.currentSpec?.workAgreement.clientPolicies) ||
      Masked.isMasked(newBuilder._specStack[0]?.workAgreement.clientPolicies)
    ) {
      throw new Error("Cannot set client policy documents when current client policy documents is masked");
    }

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        clientPolicies: _.uniqBy(documents, (document) => document.id.value),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.name !== ProposalFieldName.ClientPolicies,
    );
    for (const document of documents) {
      const existingDocument = newBuilder._specStack[0].workAgreement.clientPolicies?.some((doc) =>
        doc.id.isEqualTo(document.id),
      );
      if (!existingDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ClientPolicy(document.id),
          ProposalChangeAction.Add,
          document.name?.value ?? "",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const document of newBuilder._specStack[0].workAgreement.clientPolicies ?? []) {
      const removedDocument = !documents.some((doc) => doc.id.isEqualTo(document.id));
      if (removedDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ClientPolicy(document.id),
          ProposalChangeAction.Remove,
          document.name?.value ?? "",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    return newBuilder;
  }

  public setVendorPolicies(documents: WorkDocument[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    if (
      Masked.isMasked(newBuilder.currentSpec?.workAgreement.vendorPolicies) ||
      Masked.isMasked(newBuilder._specStack[0]?.workAgreement.vendorPolicies)
    ) {
      throw new Error("Cannot set vendor policy documents when current vendor policy documents is masked");
    }

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        vendorPolicies: _.uniqBy(documents, (document) => document.id.value),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.name !== ProposalFieldName.VendorPolicies,
    );
    for (const document of documents) {
      const existingDocument = newBuilder._specStack[0]?.workAgreement.vendorPolicies?.some((doc) =>
        doc.id.isEqualTo(document.id),
      );
      if (!existingDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.VendorPolicy(document.id),
          ProposalChangeAction.Add,
          document.name?.value ?? "",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const document of newBuilder._specStack[0]?.workAgreement.vendorPolicies ?? []) {
      const removedDocument = !documents.some((doc) => doc.id.isEqualTo(document.id));
      if (removedDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.VendorPolicy(document.id),
          ProposalChangeAction.Remove,
          document.name?.value ?? "",
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    return newBuilder;
  }

  public setClientTeamTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        clientTeamTemplateIds: _.uniqBy(templateIds, (id) => id.value),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setVendorTeamTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        vendorTeamTemplateIds: _.uniqBy(templateIds, (id) => id.value),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setClientFeeScheduleTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        clientFeeScheduleTemplateIds: _.uniqBy(templateIds, (id) => id.value),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setVendorFeeScheduleTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        vendorFeeScheduleTemplateIds: _.uniqBy(templateIds, (id) => id.value),
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setConflictsCheckWaived(waived: AHBoolean): ProposalBuilder {
    if (
      Masked.isMasked(this._specStack[0]?.workAgreement.conflictsCheckWaived) ||
      Masked.isMasked(this.currentSpec?.workAgreement.conflictsCheckWaived) ||
      this.currentSpec?.workAgreement.conflictsCheckWaived?.value === waived?.value
    ) {
      return this;
    }
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    if (Masked.isMasked(newBuilder._specStack[0]?.workAgreement.conflictsCheckWaived))
      throw new Error("Cannot compare conflicts check waived changes when original is masked");
    if (Masked.isMasked(newBuilder.currentSpec?.workAgreement.conflictsCheckWaived))
      throw new Error("Cannot compare conflicts check waived changes when current is masked");
    if (Masked.isMasked(newBuilder._specStack[0]?.workAgreement.conflicts))
      throw new Error("Cannot compare conflicts check waived changes when original conflicts documents are masked");
    if (Masked.isMasked(newBuilder.currentSpec?.workAgreement.conflicts))
      throw new Error("Cannot compare conflicts check waived changes when current conflicts documents are masked");

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        conflictsCheckWaived: waived,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.WaiveConflictsCheck),
    );
    if (newBuilder._specStack[0]?.workAgreement.conflictsCheckWaived?.value === waived?.value) {
      return newBuilder;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.WaiveConflictsCheck,
      waived.value ? ProposalChangeAction.Add : ProposalChangeAction.Remove,
      "Conflicts Check Waiver",
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setTeamRestricted(teamRestricted: AHBoolean): ProposalBuilder {
    if (
      Masked.isMasked(this._specStack[0]?.workAgreement.teamRestricted) ||
      Masked.isMasked(this.currentSpec?.workAgreement.teamRestricted) ||
      this.currentSpec?.workAgreement.teamRestricted?.value === teamRestricted?.value
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    if (Masked.isMasked(newBuilder._specStack[0]?.workAgreement.teamRestricted)) {
      throw new Error("Cannot compare team restriction changes when original is masked");
    }

    const newSpec = {
      ...newBuilder.currentSpec,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
        teamRestricted: teamRestricted,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.TeamRestriction),
    );
    if (newBuilder._specStack[0]?.workAgreement.teamRestricted?.value === teamRestricted?.value) {
      return newBuilder;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.TeamRestriction,
      teamRestricted.value ? ProposalChangeAction.Add : ProposalChangeAction.Remove,
      "Team Restriction",
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setClientReviewers(newReviewers: ProposalReviewer[]): ProposalBuilder {
    if (
      this.currentSpec.clientReviewers?.length === newReviewers.length &&
      this.currentSpec.clientReviewers.every((currentReviewer) =>
        newReviewers.some(
          (r) =>
            r.userId.value == currentReviewer.entityMember.userId.value &&
            r.entityId.value == currentReviewer.entityMember.legalEntityId.value &&
            r.canEdit == currentReviewer.canEdit,
        ),
      )
    ) {
      return this;
    }
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    const newSpec = {
      ...newBuilder.currentSpec,
      clientReviewers: newReviewers,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setVendorReviewers(reviewers: ProposalReviewer[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    const newSpec = {
      ...newBuilder.currentSpec,
      vendorReviewers: reviewers,
      workAgreement: {
        ...newBuilder.currentSpec.workAgreement,
      },
    };

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public buildNewDraft(): Proposal {
    return new Proposal({
      spec: this.currentSpec,
      currentUser: this._currentUser,
    });
  }

  public updateDraftProposal(originalProposal: Proposal): Proposal {
    return new Proposal({
      originalProposal,
      spec: this.currentSpec,
      currentUser: this._currentUser,
    });
  }

  public toJSON(): ProposalBuilderJSON {
    return new ProposalBuilderSchema(new WorkAgreementSchema(), this._currentUser).serialize({
      ...this,
      currentIndex: this._currentIndex,
      specStack: this._specStack,
      sessionHistory: this._sessionHistory.map((change) => ({
        field: change.field,
        action: change.action.toString(),
        value: change.value,
      })),
      lastUpdated: this._lastUpdated,
    });
  }

  public static fromJSON(params: { json: unknown; session: Session }): ProposalBuilder | null | undefined {
    const { json, session } = params;
    if (isNullOrUndefined(json)) return json;
    const result = new ProposalBuilderSchema(new WorkAgreementSchema(), session.user!).deserialize(
      typeof json === "string" ? JSON.parse(json) : json,
    );
    return new ProposalBuilder({
      ...result,
      session,
    });
  }

  public clone(): ProposalBuilder {
    const clone = new ProposalBuilder(this);
    clone._specStack = [...this._specStack];
    clone._sessionHistory = this._sessionHistory.map((change) => change.clone());
    clone._currentIndex = this._currentIndex;
    return clone;
  }

  public mergeUpdatedSpec(updatedSpec: ProposalParams): ProposalBuilder {
    let newBuilder = new ProposalBuilder(this);

    newBuilder._specStack.push(updatedSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    const originalName = this._specStack[0].workAgreement.name;
    const nameUnchanged =
      (!updatedSpec.workAgreement.name && !originalName) || updatedSpec.workAgreement.name?.isEqualTo(originalName);
    if (nameUnchanged && this.currentSpec.workAgreement.name) {
      newBuilder = newBuilder.setName(this.currentSpec.workAgreement.name);
    }

    const originalDescription = this._specStack[0].workAgreement.description;
    const descriptionUnchanged =
      (!updatedSpec.workAgreement.description && !originalDescription) ||
      updatedSpec.workAgreement.description?.isEqualTo(originalDescription);
    if (descriptionUnchanged && this.currentSpec.workAgreement.description) {
      newBuilder = newBuilder.setDescription(this.currentSpec.workAgreement.description);
    }

    const originalNegotiable = this._specStack[0].negotiable;
    const negotiableUnchanged =
      (!updatedSpec.negotiable && !originalNegotiable) || updatedSpec.negotiable === originalNegotiable;
    if (negotiableUnchanged && this.currentSpec.negotiable !== undefined) {
      newBuilder = newBuilder.setNegotiable(this.currentSpec.negotiable.value);
    }

    const originalResponseDueBy = this._specStack[0].responseDueBy;
    const responseDueByUnchanged =
      (!updatedSpec.responseDueBy && !originalResponseDueBy) ||
      updatedSpec.responseDueBy?.isEqualTo(originalResponseDueBy);
    if (responseDueByUnchanged) {
      newBuilder = newBuilder.setResponseDueBy(this.currentSpec.responseDueBy);
    }

    const originalStartDate = this._specStack[0].workAgreement?.startDate;
    const startDateUnchanged =
      (!updatedSpec.workAgreement.startDate && !originalStartDate) ||
      updatedSpec.workAgreement.startDate?.isEqualTo(originalStartDate);
    if (startDateUnchanged) {
      newBuilder = newBuilder.setStartDate(this.currentSpec.workAgreement?.startDate);
    }

    const originalEndDate = this._specStack[0].workAgreement?.endDate;
    const endDateUnchanged =
      (!updatedSpec.workAgreement.endDate && !originalEndDate) ||
      updatedSpec.workAgreement.endDate?.isEqualTo(originalEndDate);
    if (endDateUnchanged) {
      newBuilder = newBuilder.setEndDate(this.currentSpec.workAgreement?.endDate);
    }

    const originalTeam = this._specStack[0].workAgreement?.vendorTeam;
    const teamUnchanged =
      Masked.isMasked(updatedSpec.workAgreement.vendorTeam) ||
      (!updatedSpec.workAgreement.vendorTeam && !originalTeam) ||
      updatedSpec.workAgreement.vendorTeam?.isEqualTo(originalTeam);
    if (teamUnchanged && this.currentSpec.workAgreement?.vendorTeam instanceof VendorTeam) {
      newBuilder = newBuilder.setVendorTeam(this.currentSpec.workAgreement.vendorTeam);
    } else if (
      this.currentSpec.workAgreement?.vendorTeam instanceof VendorTeam &&
      originalTeam instanceof VendorTeam &&
      updatedSpec.workAgreement.vendorTeam instanceof VendorTeam
    ) {
      newBuilder = newBuilder.setVendorTeam(
        ProposalBuilder.mergeTeams(
          originalTeam,
          this.currentSpec.workAgreement?.vendorTeam,
          updatedSpec.workAgreement.vendorTeam,
        ),
      );
    }

    const originalFeeSchedule = this._specStack[0].workAgreement?.feeSchedule;
    const feeScheduleUnchanged =
      Masked.isMasked(updatedSpec.workAgreement.feeSchedule) ||
      Masked.isMasked(originalFeeSchedule) ||
      (!updatedSpec.workAgreement.feeSchedule && !originalFeeSchedule) ||
      (updatedSpec.workAgreement.feeSchedule?.every((cat) =>
        originalFeeSchedule?.some((originalCat) => originalCat.isEqualTo(cat)),
      ) &&
        originalFeeSchedule?.every(
          (cat) =>
            Masked.isNotMasked(updatedSpec.workAgreement.feeSchedule) && // ts can't track checks outside nested function
            updatedSpec.workAgreement.feeSchedule?.some((newCat) => newCat.isEqualTo(cat)),
        ));

    if (feeScheduleUnchanged && Masked.isNotMasked(this.currentSpec.workAgreement?.feeSchedule)) {
      newBuilder = newBuilder.setFeeSchedule(this.currentSpec.workAgreement?.feeSchedule);
    } else if (
      Masked.isNotMasked(this.currentSpec.workAgreement?.feeSchedule) &&
      Masked.isNotMasked(originalFeeSchedule) &&
      Masked.isNotMasked(updatedSpec.workAgreement.feeSchedule)
    ) {
      newBuilder = newBuilder.setFeeSchedule(
        ProposalBuilder.mergeFeeSchedules(
          originalFeeSchedule ?? [],
          this.currentSpec.workAgreement?.feeSchedule ?? [],
          updatedSpec.workAgreement.feeSchedule ?? [],
        ),
      );
    }

    const originalDiscount = this._specStack[0].workAgreement.discount;
    const discountUnchanged =
      Masked.isMasked(updatedSpec.workAgreement.discount) ||
      (!updatedSpec.workAgreement.discount && !originalDiscount) ||
      (Masked.isNotMasked(originalDiscount) && updatedSpec.workAgreement.discount?.isEqualTo(originalDiscount));
    if (discountUnchanged && Masked.isNotMasked(this.currentSpec.workAgreement?.discount)) {
      newBuilder = newBuilder.setDiscount(this.currentSpec.workAgreement?.discount ?? new Percent(0));
    }

    const originalConflicts = this._specStack[0].workAgreement?.conflicts;
    if (
      Masked.isNotMasked(originalConflicts) &&
      Masked.isNotMasked(updatedSpec.workAgreement.conflicts) &&
      Masked.isNotMasked(this.currentSpec.workAgreement?.conflicts)
    ) {
      const conflictsUnchanged =
        (!updatedSpec.workAgreement.conflicts && !originalConflicts) ||
        (updatedSpec.workAgreement.conflicts?.every((doc) =>
          originalConflicts?.some((originalDoc) => originalDoc.is(doc)),
        ) &&
          originalConflicts?.every(
            (doc) =>
              Masked.isNotMasked(updatedSpec.workAgreement.conflicts) && // ts can't track checks outside nested function
              updatedSpec.workAgreement.conflicts?.some((newDoc) => newDoc.is(doc)),
          ));
      if (conflictsUnchanged) {
        newBuilder = newBuilder.setConflicts(this.currentSpec.workAgreement?.conflicts ?? []);
      } else {
        newBuilder = newBuilder.setConflicts(
          ProposalBuilder.mergeDocuments(
            originalConflicts ?? [],
            this.currentSpec.workAgreement?.conflicts ?? [],
            updatedSpec.workAgreement.conflicts ?? [],
          ),
        );
      }
    }

    const originalClientPolicies = this._specStack[0].workAgreement?.clientPolicies ?? [];

    if (
      Masked.isNotMasked(originalClientPolicies) &&
      Masked.isNotMasked(updatedSpec.workAgreement.clientPolicies) &&
      Masked.isNotMasked(this.currentSpec.workAgreement.clientPolicies)
    ) {
      const clientPoliciesUnchanged =
        (!updatedSpec.workAgreement.clientPolicies && !originalClientPolicies) ||
        (updatedSpec.workAgreement.clientPolicies?.every((doc) =>
          originalClientPolicies?.some((originalDoc) => originalDoc.is(doc)),
        ) &&
          originalClientPolicies?.every(
            (doc) =>
              Masked.isNotMasked(updatedSpec.workAgreement.clientPolicies) && // ts can't track checks outside nested function
              updatedSpec.workAgreement.clientPolicies?.some((newDoc) => newDoc.is(doc)),
          ));
      if (clientPoliciesUnchanged) {
        newBuilder = newBuilder.setClientPolicies(this.currentSpec.workAgreement?.clientPolicies ?? []);
      } else {
        newBuilder = newBuilder.setClientPolicies(
          ProposalBuilder.mergeDocuments(
            originalClientPolicies,
            this.currentSpec.workAgreement?.clientPolicies ?? [],
            updatedSpec.workAgreement.clientPolicies ?? [],
          ),
        );
      }
    }

    const originalVendorPolicies = this._specStack[0].workAgreement?.vendorPolicies ?? [];

    if (
      Masked.isNotMasked(originalVendorPolicies) &&
      Masked.isNotMasked(updatedSpec.workAgreement.vendorPolicies) &&
      Masked.isNotMasked(this.currentSpec.workAgreement?.vendorPolicies)
    ) {
      const vendorPoliciesUnchanged =
        (!updatedSpec.workAgreement.vendorPolicies && !originalVendorPolicies) ||
        (updatedSpec.workAgreement.vendorPolicies?.every((doc) =>
          originalVendorPolicies?.some((originalDoc) => originalDoc.is(doc)),
        ) &&
          originalVendorPolicies?.every(
            (doc) =>
              Masked.isNotMasked(updatedSpec.workAgreement.vendorPolicies) && // ts can't track checks outside nested function
              updatedSpec.workAgreement.vendorPolicies?.some((newDoc) => newDoc.is(doc)),
          ));
      if (vendorPoliciesUnchanged) {
        newBuilder = newBuilder.setVendorPolicies(
          ProposalBuilder.mergeDocuments(
            originalVendorPolicies,
            this.currentSpec.workAgreement?.vendorPolicies ?? [],
            updatedSpec.workAgreement.vendorPolicies ?? [],
          ),
        );
      }
    }

    const originalClientReviewers = this._specStack[0].clientReviewers;
    const clientReviewersUnchanged =
      (!updatedSpec.clientReviewers && !originalClientReviewers) ||
      (updatedSpec.clientReviewers?.every((reviewer) =>
        originalClientReviewers?.some((originalReviewer) =>
          originalReviewer.entityMember.userId.isEqualTo(reviewer.entityMember.userId),
        ),
      ) &&
        originalClientReviewers?.every((reviewer) =>
          updatedSpec.clientReviewers?.some((newReviewer) =>
            newReviewer.entityMember.userId.isEqualTo(reviewer.entityMember.userId),
          ),
        ));
    if (clientReviewersUnchanged && this.currentSpec.clientReviewers) {
      newBuilder = newBuilder.setClientReviewers(
        this.currentSpec.clientReviewers.map(
          (reviewerSpec) =>
            new ProposalReviewer({
              ...reviewerSpec,
            }),
        ),
      );
    }

    const originalVendorReviewers = this._specStack[0].vendorReviewers;
    const vendorReviewersUnchanged =
      (!updatedSpec.vendorReviewers && !originalVendorReviewers) ||
      (updatedSpec.vendorReviewers?.every((reviewer) =>
        originalVendorReviewers?.some((originalReviewer) =>
          originalReviewer.entityMember.userId.isEqualTo(reviewer.entityMember.userId),
        ),
      ) &&
        originalVendorReviewers?.every((reviewer) =>
          updatedSpec.vendorReviewers?.some((newReviewer) =>
            newReviewer.entityMember.userId.isEqualTo(reviewer.entityMember.userId),
          ),
        ));
    if (vendorReviewersUnchanged && this.currentSpec.vendorReviewers) {
      newBuilder = newBuilder.setVendorReviewers(
        this.currentSpec.vendorReviewers.map(
          (reviewerSpec) =>
            new ProposalReviewer({
              ...reviewerSpec,
            }),
        ),
      );
    }

    return newBuilder;
  }

  protected _specStack: ProposalParams[] = [];
  protected _lastUpdated: AHDate;

  private static mergeDocuments(
    originalDocuments: WorkDocument[],
    currentDocuments: WorkDocument[],
    newDocuments: WorkDocument[],
  ): WorkDocument[] {
    const removedDocuments: WorkDocument[] = [];
    for (const originalDocument of originalDocuments ?? []) {
      const documentRemoved =
        !newDocuments.some((newDocument) => newDocument.is(originalDocument)) ||
        !currentDocuments.some((currentDocument) => currentDocument.is(originalDocument));
      if (documentRemoved) {
        removedDocuments.push(originalDocument);
      }
    }

    const mergedDocuments = newDocuments.filter(
      (updatedDocument) => !removedDocuments.some((removedDocument) => removedDocument.is(updatedDocument)),
    );
    for (const document of currentDocuments ?? []) {
      if (
        !mergedDocuments?.some((newDocument) => newDocument.is(document)) &&
        !removedDocuments.some((removedDocument) => removedDocument.is(document))
      ) {
        mergedDocuments?.push(document);
      }
    }
    return mergedDocuments;
  }

  private static mergeTeams(originalTeam?: VendorTeam, currentTeam?: VendorTeam, newTeam?: VendorTeam): VendorTeam {
    let leader = newTeam?.leader;
    const leaderUnchanged =
      (!newTeam?.leader && !originalTeam?.leader) || newTeam?.leader?.isEqualTo(originalTeam?.leader);
    if (leaderUnchanged) {
      leader = currentTeam?.leader;
    }

    const removedMembers: EntityMember[] = [];
    for (const originalMember of originalTeam?.members ?? []) {
      const memberRemoved =
        !newTeam?.members.some((newMember) => newMember.isEqualTo(originalMember)) ||
        !currentTeam?.members.some((currentMember) => currentMember.isEqualTo(originalMember));
      if (memberRemoved) {
        removedMembers.push(originalMember);
      }
    }

    const mergedMembers = newTeam?.members.filter(
      (newMember) => !removedMembers.some((removedMember) => removedMember.isEqualTo(newMember)),
    );
    for (const member of currentTeam?.members ?? []) {
      if (
        !mergedMembers?.some((newMember) => newMember.isEqualTo(member)) &&
        !removedMembers.some((removedMember) => removedMember.isEqualTo(member))
      ) {
        mergedMembers?.push(member);
      }
    }
    return new VendorTeam({ leader, members: mergedMembers ?? [] });
  }

  private static mergeFeeSchedules(
    originalFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    currentFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    newFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
  ): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] {
    const removedCategories: FeeScheduleCategory[] = [];
    const removedCategoriesMasked: FeeScheduleCategoryMaskedCurrency[] = [];
    for (const originalCategory of originalFeeSchedule ?? []) {
      const categoryRemoved =
        !newFeeSchedule?.some((newCategory) => newCategory.id.isEqualTo(originalCategory.id)) ||
        !currentFeeSchedule?.some((currentCategory) => currentCategory.id.isEqualTo(originalCategory.id));

      const currentCategory = currentFeeSchedule?.find((currentCategory) =>
        currentCategory.id.isEqualTo(originalCategory.id),
      );
      const modifiedLocally = currentCategory && !currentCategory.isEqualTo(originalCategory);

      // noinspection SuspiciousTypeOfGuard
      if (categoryRemoved && !modifiedLocally && originalCategory instanceof FeeScheduleCategory) {
        removedCategories.push(originalCategory);
      } else if (categoryRemoved && !modifiedLocally) {
        removedCategoriesMasked.push(originalCategory);
      }
    }

    const mergedCategories = newFeeSchedule.filter(
      (updatedCategory) =>
        !removedCategories.some((removedCategory) => removedCategory.id.isEqualTo(updatedCategory.id)) &&
        !removedCategoriesMasked.some((removedCategory) => removedCategory.id.isEqualTo(updatedCategory.id)),
    );
    for (const category of currentFeeSchedule ?? []) {
      if (
        !mergedCategories?.some((newCategory) => newCategory.id.isEqualTo(category.id)) &&
        !removedCategories.some((removedCategory) => removedCategory.id.isEqualTo(category.id)) &&
        !removedCategoriesMasked.some((removedCategory) => removedCategory.id.isEqualTo(category.id))
      ) {
        mergedCategories?.push(category);
      }
    }
    return mergedCategories;
  }

  private updateCurrentIndex() {
    if (this._currentIndex < this._specStack.length - 1) {
      this._specStack.push(this._specStack[this._currentIndex]);
    }
    this._currentIndex = this._specStack.length - 1;
  }

  private readonly _currentUser: User;
  private readonly _proposalSchema: ProposalSchema;

  private _currentIndex: number = 0;
  private _sessionHistory: ProposalSpecChange[] = [];
}
